import { useEffect, useMemo, useState } from 'react';
import { CloseButton, FormCheck, Modal } from 'react-bootstrap';

import { generateWordSearchRegex } from '../../functions/StringParsing';

import { Activity } from '../../types/Quality/Activity';

const INACTIVE_LABEL_SUFFIX = ' (inactive)';

type ActivitySearchProps = {
    showModal: boolean,
    activityList: Activity[],
    onClose: () => void,
    onAddClick: (name: string) => void,
};

const ActivitySearch = ( { showModal, activityList, onClose, onAddClick }:ActivitySearchProps) => {
    const decoratedActivityList = useMemo<Activity[]>(() => (
        activityList.map(activity => (
            activity.isActive
                ? activity
                : { ...activity, name: `${activity.name}${INACTIVE_LABEL_SUFFIX}` }
        ))
    ), [activityList]);
    const [searchOverridden, setSearchOverridden] = useState<boolean>(false);
    const [filteredList, setFilteredList] = useState<Activity[]>([]);
    const [newActivityName, setNewActivityName] = useState<string>('');

    useEffect(() => {
        // Only show filtered results if the user has typed something
        if (newActivityName.trim().length === 0) {
            setFilteredList([]);
            return;
        }
        
        const regex = generateWordSearchRegex(newActivityName, { ignoreFillerWords: false });
        const newList: Activity[] = decoratedActivityList.filter((a) => regex.test(a.nameNormalized));

        setFilteredList(newList);
    }, [decoratedActivityList, newActivityName]);

    const handleInput = (input: string) => {
        setNewActivityName(input);
    };

    const handleOverrideToggle = () => {
        setSearchOverridden(!searchOverridden);
    };

    const closeActivitySearch = () => {
        // TODO (johns) revisit this to figure out if we can stop th
        //   the refresh of the list before we close
        onClose();
        setNewActivityName('');
        setFilteredList([]);
        setSearchOverridden(false);
    };

    const handleAddClick = () => {
        closeActivitySearch();
        onAddClick(newActivityName);
    };

    return (
        <Modal
            show={showModal}
            keyboard={false}
        >
            <Modal.Header>
                <div className='col'>
                    <div className='row'>
                        <div className='col'>
                        </div>
                        <div className='col-9 h2 text-center'>
                            Duplicate Prevention
                        </div>
                        <div className='col'>
                            <CloseButton 
                                onClick={() => closeActivitySearch()}
                            />
                        </div>
                    </div>
                    <div className='row'>
                        <div className='col'>
                            <ol>
                                <li>Enter the new activity name in the <i>SEARCH</i> box below.</li>
                                <li>Check the <i>Activity List</i> for any existing ones that match.</li>
                                <li>If an existing activity matches, please do NOT add a duplicate. Instead, edit the existing activity if needed.</li>
                                <b><li>If the activity is truly new (no matches), check the <i>Not A Duplicate</i> box then select <i>Add New</i>.</li></b>
                            </ol>
                        </div>
                    </div>
                </div>
            </Modal.Header>
            <Modal.Body>
                <div className='row border rounded-2 shadow-sm p-2'>
                    <div className='col'>
                        <div className='row'>
                            <input className='form-control'
                                style={{ textTransform: 'uppercase'}}
                                type='text'
                                onChange={(e) => handleInput(e.target.value.trim())}
                                placeholder={'Search...'}
                            />
                        </div>
                        <div className='row' style={{ maxHeight: '300px', overflowY: 'auto' }}>
                            <table
                                className='benchmark-activities-table my-2'
                            >
                                <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                                    <tr>
                                        <th className='benchmark-activity-header'>
                                            Activity List
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        filteredList.map((activity) => (
                                        <tr
                                            className='border-bottom'
                                            key={`${activity.name}`}
                                        >
                                            <td className='px-2'>
                                                {activity.name}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <div className='col'>
                    <div className='row mb-2'>
                        <div className='col text-center'>
                            <FormCheck
                                type='checkbox'
                                id={'override-search'}
                                label={'Not A Duplicate'}
                                checked={searchOverridden}
                                disabled={!newActivityName || filteredList.length === 0}
                                onChange={() => handleOverrideToggle()}
                                style={{ display: 'inline-block' }}
                            />
                        </div>
                    </div>
                    <div className='row'>
                        <div className='col text-center'>
                            <button
                                className='btn btn-primary'
                                onClick={() => handleAddClick()}
                                disabled={!newActivityName || (filteredList.length !== 0 && !searchOverridden)}
                            >
                                Add New
                            </button>
                        </div>
                    </div>
                </div>
            </Modal.Footer>
        </Modal>
    );
};

export default ActivitySearch;